import * as React from 'react';

import { cn } from '@heidi/utils';

import type { FadeProps, FadeSide } from './fade.types';

/** Mask gradient direction for each side (opaque at edge, transparent inward). */
const maskDirectionMap: Record<FadeSide, string> = {
  top: 'to bottom',
  bottom: 'to top',
  left: 'to right',
  right: 'to left',
};

/**
 * Eased opacity stops for smooth gradient falloff.
 * Each entry: [position within fade zone (0–1), opacity (0–1)].
 * Follows an ease-out curve for natural, non-linear transitions.
 */
const FADE_CURVE: ReadonlyArray<[number, number]> = [
  [0.15, 0.85],
  [0.3, 0.65],
  [0.48, 0.45],
  [0.62, 0.28],
  [0.78, 0.13],
  [0.92, 0.04],
];

function buildEasedMask(direction: string): string {
  const stops = FADE_CURVE.map(
    ([pos, alpha]) =>
      `rgba(0,0,0,${alpha}) calc(var(--stop) + (100% - var(--stop)) * ${pos})`
  ).join(', ');

  return `linear-gradient(${direction}, black 0%, black var(--stop), ${stops}, transparent 100%)`;
}

export const Fade = React.forwardRef<HTMLDivElement, FadeProps>(
  ({ stop, blur, side = 'top', className, background, style, debug, ...rest }, ref) => {
    const direction = maskDirectionMap[side];
    const resolvedBlur = blur ?? '0px';
    const hasBlur = resolvedBlur !== '0px' && resolvedBlur !== '0';

    return (
      <div
        ref={ref}
        aria-hidden
        data-fade
        data-side={side}
        className={cn('pointer-events-none absolute select-none', className)}
        style={
          {
            '--stop': stop ?? '0%',
            '--background': background,
            ...(hasBlur && { backdropFilter: `blur(${resolvedBlur})` }),
            background: 'var(--background)',
            maskImage: buildEasedMask(direction),
            ...(debug && { outline: '2px solid orange' }),
            ...style,
          } as React.CSSProperties
        }
        {...rest}
      />
    );
  }
);

Fade.displayName = 'Fade';
